<template>
  <section class="hero-section" id="hero-section">
    <picture class="ppdesk">
      <source media="(min-width: 1024px)" :srcset="heroImage" :alt="heroTitle">
      <source media="(min-width: 650px)" :srcset="heroImage" :alt="heroTitle">
      <source media="(min-width: 320px)" :srcset="heroImage" :alt="heroTitle">
      <img :src="heroImage" :alt="heroTitle" class="banner-expo">
    </picture>
    <div class="container">
      <div class="row">
        <div class="col-md-12">
          <h1 class="hero-desktop" v-html="heroTitle"></h1>
          <h2 v-html="heroSubtitle" class="hero-subtitle"></h2>
        </div>
        <div class="col-md-12">
          <div class="hero-search" id="hero-search">
            <form class="form-horizontal" @submit.prevent="handleHeroSearch">
              <ul class="hero-search-main">
                <li class="p-0 m-0">
                  <select name="mc" aria-label="Select Category"
                    class="form-control form-control-custom dropdown-toogle-icon" id="getMainCategoryDataHeader1"
                    v-model="searchCategory">
                    <option value="" hidden>Select Category</option>
                    <option v-for="cat in uniqueCategories" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
                  </select>
                </li>
                <li class="p-0 m-0">
                  <select name="min_cost" aria-label="Select Min Investment"
                    class="form-control form-control-custom dropdown-toogle-icon" id="minAmount1"
                    v-model="searchMinInvestment" @change="handleMinInvestmentChange">
                    <option value="" hidden>Select Min Investment</option>
                    <option v-for="min in uniqueMinInvestments" :key="min.value" :value="min.value">{{ min.label }}
                    </option>
                  </select>
                </li>
                <li class="p-0 m-0">
                  <select name="max_cost" aria-label="Select Max Investment"
                    class="form-control form-control-custom dropdown-toogle-icon" id="maxAmount1"
                    v-model="searchMaxInvestment" @change="handleMaxInvestmentChange">
                    <option value="" hidden>Select Max Investment</option>
                    <option v-for="max in uniqueMaxInvestments" :key="max.value" :value="max.value">{{ max.label }}
                    </option>
                  </select>
                </li>
                <li class="p-0 m-0"><button type="submit" class="btn btn-main btn-main-hero" id="seo-hero-inv-btn"
                    aria-label="Submit Brand Search"><i class="search-icon fa fa-search" aria-hidden="true"></i><span
                      class="smobile">Search</span></button>
                </li>
              </ul>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useFranchiseStore } from '@/stores/franchiseStore'
import { formatSingleInvestment } from '@/utils/formatters'

const router = useRouter()
const searchQuery = ref('')
const searchType = ref('category')
const searchLocation = ref('')
const searchInvestment = ref('')
const searchCategory = ref('')
const searchSubCategory = ref('')
const searchService = ref('')
const searchMinInvestment = ref('')
const searchMaxInvestment = ref('')
const searchState = ref('')
const searchCity = ref('')

// Add state for categories
// Remove categories, categoriesLoading, categoriesError, fetchCategories

const props = defineProps({
  heroImage: {
    type: String,
    default: () => require('@/assets/dummy.jpg')
  },
  heroTitle: {
    type: String,
    default: 'Search from <span>1000+ Business </span>options'
  },
  heroSubtitle: {
    type: String,
    default: "World's highest visited franchise website network"
  },
})

const overlayStyle = computed(() => ({
  background: props.overlayColor,
  opacity: props.overlayOpacity,
}))

const franchiseStore = useFranchiseStore()

// Function to fetch categories from API
// Remove categories, categoriesLoading, categoriesError, fetchCategories

onMounted(async () => {
  // Fetch categories
  // Remove categories, categoriesLoading, categoriesError, fetchCategories

  // Fetch franchises if needed
  if (!franchiseStore.franchises.length) {
    await franchiseStore.fetchFranchises()
  }
})

const uniqueCategories = computed(() => {
  // Only use franchiseStore.franchises
  const allCategories = franchiseStore.franchises.map(f => f.category)
  const validCategories = allCategories.filter(cat =>
    cat &&
    typeof cat === 'string' &&
    cat.trim() !== '' &&
    cat !== 'null' &&
    cat !== 'undefined'
  )
  const uniqueCats = [...new Set(validCategories)]
  return uniqueCats.map(cat => ({
    value: cat,
    label: cat
  }))
})

const uniqueMinInvestments = computed(() => {
  const mins = franchiseStore.franchises.map(f => f.investment_min).filter(Boolean)
  return [...new Set(mins)]
    .sort((a, b) => Number(a) - Number(b))
    .map(min => ({
      value: min,
      label: formatSingleInvestment(min)
    }))
})

const uniqueMaxInvestments = computed(() => {
  const maxs = franchiseStore.franchises.map(f => f.investment_max).filter(Boolean)
  let filteredMaxs = [...new Set(maxs)]

  // Filter max investments to only show values greater than selected min investment
  if (searchMinInvestment.value) {
    const minValue = Number(searchMinInvestment.value)
    filteredMaxs = filteredMaxs.filter(max => Number(max) > minValue)
  }

  return filteredMaxs
    .sort((a, b) => Number(a) - Number(b))
    .map(max => ({
      value: max,
      label: formatSingleInvestment(max)
    }))
})

// Validation: Ensure min investment is less than max investment
watch(searchMinInvestment, (newMin) => {
  if (newMin && searchMaxInvestment.value) {
    const minValue = Number(newMin)
    const maxValue = Number(searchMaxInvestment.value)

    if (maxValue <= minValue) {
      searchMaxInvestment.value = ''
    }
  }
})

watch(searchMaxInvestment, (newMax) => {
  if (newMax && searchMinInvestment.value) {
    const maxValue = Number(newMax)
    const minValue = Number(searchMinInvestment.value)

    if (maxValue <= minValue) {
      searchMinInvestment.value = ''
    }
  }
})

const handleMinInvestmentChange = (event) => {
  searchMinInvestment.value = event.target.value
}

const handleMaxInvestmentChange = (event) => {
  searchMaxInvestment.value = event.target.value
}

function handleHeroSearch() {
  // Build query params for /franchises
  const params = {}
  if (searchCategory.value) params.category = searchCategory.value
  if (searchMinInvestment.value) params.investment = `${searchMinInvestment.value}-` + (searchMaxInvestment.value || '')
  if (searchMaxInvestment.value && !searchMinInvestment.value) params.investment = `0-${searchMaxInvestment.value}`
  // Navigate to /franchises with query
  router.push({ path: '/franchises', query: params })
}
</script>
<style scoped>
.hero-section {
  position: relative;
  overflow: hidden;
  background: transparent !important;
  min-height: 500px;
  display: flex;
  align-items: flex-end;
  padding: 0;
  margin: 0;
  /* Remove negative margin to eliminate gap */
  border: none;
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.hero-subtitle {
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  top: -50px;
  position: relative;
}

/* Remove margin from the top of the first element after the navbar */
.hero-section:first-child {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Remove background from any parent wrapper if possible (handled in parent, but safe here) */
:global(body),
:global(#app),
:global(.main-content),
:global(.page-wrapper) {
  background: transparent !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.banner-expo,
.ppdesk {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100%;
  min-height: 100%;
  object-fit: cover;
  z-index: 0;
  pointer-events: none;
}



/* Remove any overlay */
.hero-overlay {
  display: none !important;
}

.container,
.row,
.col-md-12,
.hero-desktop,
h2,
.hero-search {
  position: relative;
  z-index: 2;
  color: white;
}

.hero-desktop {
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  top: -50px;
  position: relative;
}

h2 {
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  top: -50px;
  position: relative;
}

.hero-search {
  background: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0;
  margin-top: 80px;
}

.hero-search-main {
  display: flex;
  gap: 2px;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  top: -70px;
  position: relative;
}

.hero-search-main select,
.hero-search-main button {
  height: 44px;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  padding: 0 16px;
  vertical-align: middle;
  box-sizing: border-box;

}

.form-control-custom {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #333;
  min-width: 150px;

}

.btn-main-hero {
  background: var(--primary-blue);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-main-hero:hover {
  background: #1d4ed8;
}

.search-icon {
  margin-right: 5px;
}

@media (max-width: 768px) {
  .hero-section {
    min-height: 320px !important;
    max-height: 400px !important;
    padding-bottom: 0 !important;
  }
  .container,
  .row,
  .col-md-12 {
    background: transparent !important;
  }
  .banner-expo,
  .ppdesk {
    display: block !important;
  }
  .hero-subtitle{
    visibility: hidden;
  }
  .hero-search {
    background: rgba(255,255,255,0.95) !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 24px rgba(44,62,80,0.10) !important;
    padding: 24px 8px 18px 8px !important;
    margin-top: 24px !important;
    max-width: 98vw;
    width: 100%;
  }
  .hero-search-main {
    flex-direction: column;
    gap: 18px;
    width: 100%;
    align-items: stretch;
    top: 0;
    position: static;
  }
  .form-control-custom {
    width: 100%;
    font-size: 1.1rem;
    padding: 18px 16px;
    border-radius: 8px;
    margin-bottom: 0;
  }
  .btn-main-hero {
    width: 100%;
    min-width: 120px;
    font-size: 1.2rem;
    padding: 18px 0;
    border-radius: 8px;
    margin-top: 8px;
  }
}
</style>





<!-- <style scoped>
.hero-section {
  position: relative;
  overflow: hidden;
  background: transparent !important;
  min-height: 500px;
  display: flex;
  align-items: flex-end;
  padding: 0;
  margin: -60px 0 0 0; /* Negative top margin to pull up near navbar */
  border: none;
}

/* Remove margin from the top of the first element after the navbar */
.hero-section:first-child {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Remove background from any parent wrapper if possible (handled in parent, but safe here) */
:global(body), :global(#app), :global(.main-content), :global(.page-wrapper) {
  background: transparent !important;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.banner-expo,
.ppdesk {
  position: absolute;
  top: -60px; /* Move image up by 60px */
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

/* Remove any overlay */
.hero-overlay {
  display: none !important;
}

.container, .row, .col-md-12, .hero-desktop, h2, .hero-search {
  position: relative;
  z-index: 2;
  color: white;
}

.hero-desktop {
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

h2 {
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.hero-search {
  background: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0;
  margin-top: 40px;
}

.hero-search-main {
  display: flex;
  gap: 2px;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
}

.hero-search-main select,
.hero-search-main button {
  height: 44px;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  padding: 0 16px;
  vertical-align: middle;
  box-sizing: border-box;
}

.form-control-custom {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #333;
  min-width: 150px;
}

.btn-main-hero {
  background: var(--primary-blue);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-main-hero:hover {
  background: #1d4ed8;
}

.search-icon {
  margin-right: 5px;
}

@media (max-width: 768px) {
  .hero-section {
    min-height: 350px;
    align-items: flex-end;
  }
  .hero-search-main {
    flex-direction: column;
    gap: 15px;
  }
  .form-control-custom {
    width: 100%;
    min-width: auto;
  }
  .btn-main-hero {
    width: 100%;
    min-width: 120px;
  }
}
</style> -->